import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Shield, Mail, LogOut, RefreshCw, Pause, Play, Send, Trash2,
  Users, CheckCircle2, PauseCircle, Loader2, Filter, Sparkles,
  MessageSquareQuote, Download, Check, X, Clock,
} from "lucide-react";
import { api } from "@/lib/api";

const TOKEN_KEY = "jt_admin_token";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function StatusPill({ status }) {
  const map = {
    active:    { label: "Actif",     bg: "bg-emerald-50", txt: "text-emerald-700", ring: "ring-emerald-200" },
    paused:    { label: "En pause",  bg: "bg-amber-50",   txt: "text-amber-700",   ring: "ring-amber-200" },
    completed: { label: "Terminé",   bg: "bg-slate-100",  txt: "text-slate-600",   ring: "ring-slate-200" },
  };
  const s = map[status] || map.completed;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ${s.bg} ${s.txt} ${s.ring}`}>
      {s.label}
    </span>
  );
}

function LoginView({ onAuthed }) {
  const [step, setStep] = useState("email"); // email | otp
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const requestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/request-otp", { email });
      toast.success("Si cet email est autorisé, un code a été envoyé.");
      setStep("otp");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/admin/verify-otp", { email, code });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      toast.success("Connexion réussie");
      onAuthed(r.data.token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Code invalide");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center px-4" data-testid="admin-login">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#2E4959] text-white mb-4">
            <Shield className="w-7 h-7" />
          </div>
          <h1 className="font-serif text-3xl text-[#1E2933]">Admin Jesus Talk</h1>
          <p className="text-sm text-[#6B7A85] mt-2">Accès protégé par code de vérification</p>
        </div>

        <div className="bg-white rounded-2xl border border-[#DDE3E8] p-6 md:p-8 shadow-sm">
          {step === "email" && (
            <form onSubmit={requestOtp} className="space-y-4">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-[#4A6D82]">Email admin</span>
                <input
                  data-testid="admin-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  className="mt-2 w-full px-4 py-3 rounded-full bg-white border border-[#DDE3E8] focus:ring-2 focus:ring-[#4A6D82] focus:border-[#4A6D82] outline-none"
                />
              </label>
              <button
                data-testid="admin-request-otp"
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#4A6D82] text-white text-sm hover:bg-[#2E4959] transition disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Recevoir mon code
              </button>
            </form>
          )}
          {step === "otp" && (
            <form onSubmit={verify} className="space-y-4">
              <p className="text-sm text-[#6B7A85]">
                Un code à 6 chiffres a été envoyé à <strong className="text-[#1E2933]">{email}</strong>. Il expire dans 10 min.
              </p>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-[#4A6D82]">Code de vérification</span>
                <input
                  data-testid="admin-otp-input"
                  type="text"
                  required
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  className="mt-2 w-full px-4 py-3 rounded-full bg-white border border-[#DDE3E8] focus:ring-2 focus:ring-[#4A6D82] focus:border-[#4A6D82] outline-none text-center tracking-[0.5em] font-mono text-lg"
                />
              </label>
              <button
                data-testid="admin-verify-otp"
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#4A6D82] text-white text-sm hover:bg-[#2E4959] transition disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                Se connecter
              </button>
              <button
                type="button"
                onClick={() => { setStep("email"); setCode(""); }}
                className="w-full text-xs text-[#6B7A85] hover:text-[#4A6D82] underline underline-offset-4"
              >
                ← Utiliser un autre email
              </button>
            </form>
          )}
        </div>
        <p className="text-center text-xs text-[#6B7A85] mt-6">
          © Jesus Talk · Dashboard interne
        </p>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone = "slate" }) {
  const tones = {
    slate:   "bg-slate-50 text-slate-700",
    blue:    "bg-[#E4EBEF] text-[#2E4959]",
    emerald: "bg-emerald-50 text-emerald-700",
    amber:   "bg-amber-50 text-amber-700",
  };
  return (
    <div className="bg-white border border-[#DDE3E8] rounded-2xl p-5">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-[#6B7A85]">{label}</p>
          <p className="text-2xl font-serif text-[#1E2933]" data-testid={`stat-${label}`}>{value}</p>
        </div>
      </div>
    </div>
  );
}

function TestimoniesTab({ authHeader }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(""); // "" | "true" | "false"
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "") params.approved = filter;
      const r = await api.get("/admin/testimonies", { headers: authHeader, params });
      setData(r.data);
    } catch (err) {
      toast.error("Impossible de charger les témoignages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const act = async (t, action) => {
    setBusy(t.id + action);
    try {
      if (action === "delete") {
        await api.delete(`/admin/testimonies/${t.id}`, { headers: authHeader });
        toast.success("Témoignage supprimé");
      } else {
        await api.post(
          `/admin/testimonies/${t.id}/approve`,
          { approved: action === "approve" },
          { headers: authHeader },
        );
        toast.success(action === "approve" ? "Témoignage approuvé" : "Retiré de la publication");
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="admin-testimonies-tab">
      {/* Stats */}
      {data && (
        <div className="grid grid-cols-3 gap-3 md:gap-4 mb-6">
          <StatCard icon={MessageSquareQuote} label="Total"      value={data.stats.total}    tone="blue" />
          <StatCard icon={CheckCircle2}       label="Approuvés"  value={data.stats.approved} tone="emerald" />
          <StatCard icon={Clock}              label="En attente" value={data.stats.pending}  tone="amber" />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#6B7A85]">
          <Filter className="w-3.5 h-3.5" /> Filtres
        </div>
        <select
          data-testid="filter-testimony-status"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-2 rounded-full bg-white border border-[#DDE3E8] text-sm"
        >
          <option value="">Tous</option>
          <option value="true">Approuvés (publiés)</option>
          <option value="false">En attente</option>
        </select>
        <button
          onClick={load}
          className="ml-auto inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#4A6D82] text-white text-xs hover:bg-[#2E4959] transition"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Rafraîchir
        </button>
      </div>

      {/* List */}
      <div className="bg-white border border-[#DDE3E8] rounded-2xl overflow-hidden">
        {loading && (
          <div className="flex items-center justify-center py-16 text-[#6B7A85]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement...
          </div>
        )}
        {!loading && data && data.testimonies.length === 0 && (
          <div className="text-center py-16 text-[#6B7A85]">
            <p className="font-serif text-lg">Aucun témoignage pour ces filtres.</p>
          </div>
        )}
        {!loading && data && data.testimonies.length > 0 && (
          <ul className="divide-y divide-[#DDE3E8]" data-testid="testimonies-list">
            {data.testimonies.map((t) => (
              <li key={t.id} className="p-5 md:p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-serif text-lg text-[#1E2933]">{t.name || "Anonyme"}</span>
                      {t.city && <span className="text-xs text-[#6B7A85]">· {t.city}</span>}
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ${t.approved ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-amber-50 text-amber-700 ring-amber-200"}`}>
                        {t.approved ? "Publié" : "En attente"}
                      </span>
                      {t.category && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-[#E4EBEF] text-[#2E4959]">
                          {t.category}
                        </span>
                      )}
                    </div>
                    {t.title && <p className="mt-1 text-sm font-medium text-[#4A6D82]">{t.title}</p>}
                    <p className="mt-2 text-[#1E2933] whitespace-pre-wrap leading-relaxed" data-testid={`testimony-story-${t.id}`}>
                      {t.story}
                    </p>
                    <p className="mt-3 text-xs text-[#6B7A85]">
                      Soumis le {fmtDate(t.created_at)}
                      {t.email && <span> · <a href={`mailto:${t.email}`} className="text-[#4A6D82] hover:underline">{t.email}</a></span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {!t.approved && (
                      <button
                        onClick={() => act(t, "approve")}
                        disabled={busy === t.id + "approve"}
                        title="Approuver et publier"
                        data-testid={`approve-testimony-${t.id}`}
                        className="p-2 rounded-full text-emerald-600 hover:bg-emerald-50 transition disabled:opacity-40"
                      >
                        {busy === t.id + "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      </button>
                    )}
                    {t.approved && (
                      <button
                        onClick={() => act(t, "unapprove")}
                        disabled={busy === t.id + "unapprove"}
                        title="Retirer de la publication"
                        className="p-2 rounded-full text-amber-600 hover:bg-amber-50 transition disabled:opacity-40"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (window.confirm(`Supprimer le témoignage de ${t.name || "Anonyme"} ?`)) act(t, "delete");
                      }}
                      disabled={busy === t.id + "delete"}
                      title="Supprimer"
                      className="p-2 rounded-full text-rose-600 hover:bg-rose-50 transition disabled:opacity-40"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}


function Dashboard({ token, onLogout }) {
  const [tab, setTab] = useState("parcours"); // "parcours" | "testimonies"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterParcours, setFilterParcours] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [busy, setBusy] = useState(null); // sub_id currently being acted on

  const authHeader = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterParcours) params.parcours_id = filterParcours;
      if (filterStatus) params.status = filterStatus;
      const r = await api.get("/admin/subscribers", { headers: authHeader, params });
      setData(r.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expirée");
        onLogout();
      } else {
        toast.error("Impossible de charger les abonnés");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "parcours") load();
    /* eslint-disable-next-line */
  }, [filterParcours, filterStatus, tab]);

  const act = async (sub, action) => {
    setBusy(sub.id + action);
    const endpoints = {
      retrigger: { method: "post", url: `/admin/subscribers/${sub.id}/retrigger`, msg: "Séquence relancée" },
      pause:     { method: "post", url: `/admin/subscribers/${sub.id}/pause`,     msg: "Mis en pause" },
      resume:    { method: "post", url: `/admin/subscribers/${sub.id}/resume`,    msg: "Séquence reprise" },
      sendNext:  { method: "post", url: `/admin/subscribers/${sub.id}/send-next`, msg: "Email envoyé" },
      delete:    { method: "delete", url: `/admin/subscribers/${sub.id}`,         msg: "Abonné supprimé" },
    };
    const ep = endpoints[action];
    try {
      const r = await api({ method: ep.method, url: ep.url, headers: authHeader });
      toast.success(ep.msg + (r.data?.sent === false ? " (envoi Resend échoué — retentera)" : ""));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setBusy(null);
    }
  };

  const runTick = async () => {
    setBusy("tick");
    try {
      await api.post("/admin/drip/tick", {}, { headers: authHeader });
      toast.success("Queue drip traitée");
      await load();
    } catch (err) {
      toast.error("Erreur");
    } finally {
      setBusy(null);
    }
  };

  const runMigration = async () => {
    if (!window.confirm(
      "Migrer les inscriptions historiques vers la nouvelle collection ?\n\n" +
      "Les personnes migrées recevront l'email J+0 dès le prochain tick (< 15 min).\n" +
      "Les emails déjà présents dans les abonnés seront ignorés."
    )) return;
    setBusy("migrate");
    try {
      const r = await api.post("/admin/migrate-enrollments?send_immediately=true", {}, { headers: authHeader });
      toast.success(`Migration OK : ${r.data.migrated_count} ajoutés, ${r.data.skipped_count} déjà présents`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur migration");
    } finally {
      setBusy(null);
    }
  };

  const parcoursOptions = data?.parcours_info || {};

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-[#2E4959] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <p className="font-serif text-lg leading-none">Admin · Jesus Talk</p>
              <p className="text-[11px] uppercase tracking-widest text-white/60 mt-1">Parcours par email</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {tab === "parcours" && (
              <button
                onClick={runMigration}
                disabled={busy === "migrate"}
                data-testid="admin-migrate-btn"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs bg-[#C99A5B] hover:bg-[#b0844b] transition"
                title="Migrer les inscriptions historiques"
              >
                {busy === "migrate" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                Migrer historique
              </button>
            )}
            {tab === "parcours" && (
              <button
                onClick={runTick}
                disabled={busy === "tick"}
                data-testid="admin-run-tick"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs bg-white/10 hover:bg-white/20 transition"
                title="Traiter la queue manuellement"
              >
                {busy === "tick" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Tick manuel
              </button>
            )}
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs bg-white/10 hover:bg-white/20 transition"
            >
              <LogOut className="w-3.5 h-3.5" />
              Déconnexion
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex gap-1 -mb-px">
            <button
              onClick={() => setTab("parcours")}
              data-testid="tab-parcours"
              className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition ${
                tab === "parcours"
                  ? "bg-[#F7F5F0] text-[#2E4959]"
                  : "text-white/70 hover:text-white hover:bg-white/5"
              }`}
            >
              <Users className="w-4 h-4 inline mr-1.5 -mt-0.5" />
              Parcours
            </button>
            <button
              onClick={() => setTab("testimonies")}
              data-testid="tab-testimonies"
              className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition ${
                tab === "testimonies"
                  ? "bg-[#F7F5F0] text-[#2E4959]"
                  : "text-white/70 hover:text-white hover:bg-white/5"
              }`}
            >
              <MessageSquareQuote className="w-4 h-4 inline mr-1.5 -mt-0.5" />
              Témoignages
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {tab === "testimonies" && <TestimoniesTab authHeader={authHeader} />}
        {tab === "parcours" && (<>
        {/* Stats */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
            <StatCard icon={Users}         label="Total"    value={data.stats.total}     tone="blue" />
            <StatCard icon={Sparkles}      label="Actifs"   value={data.stats.active}    tone="emerald" />
            <StatCard icon={PauseCircle}   label="En pause" value={data.stats.paused}    tone="amber" />
            <StatCard icon={CheckCircle2}  label="Terminés" value={data.stats.completed} tone="slate" />
          </div>
        )}

        {/* Parcours breakdown */}
        {data && (
          <div className="bg-white border border-[#DDE3E8] rounded-2xl p-5 md:p-6 mb-6">
            <p className="text-xs uppercase tracking-widest text-[#6B7A85] mb-3">Par parcours</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(data.parcours_info).map(([pid, info]) => (
                <div key={pid} className="p-3 rounded-xl bg-[#F7F5F0] border border-[#DDE3E8]">
                  <p className="text-[11px] uppercase tracking-widest text-[#4A6D82]">{info.subtitle}</p>
                  <p className="font-serif text-lg text-[#1E2933] leading-tight mt-0.5">{info.title}</p>
                  <p className="text-2xl font-serif text-[#2E4959] mt-2">{data.stats.by_parcours[pid] || 0}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#6B7A85]">
            <Filter className="w-3.5 h-3.5" /> Filtres
          </div>
          <select
            data-testid="filter-parcours"
            value={filterParcours}
            onChange={(e) => setFilterParcours(e.target.value)}
            className="px-3 py-2 rounded-full bg-white border border-[#DDE3E8] text-sm"
          >
            <option value="">Tous les parcours</option>
            {Object.entries(parcoursOptions).map(([pid, info]) => (
              <option key={pid} value={pid}>{info.title}</option>
            ))}
          </select>
          <select
            data-testid="filter-status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 rounded-full bg-white border border-[#DDE3E8] text-sm"
          >
            <option value="">Tous statuts</option>
            <option value="active">Actifs</option>
            <option value="paused">En pause</option>
            <option value="completed">Terminés</option>
          </select>
          <button
            onClick={load}
            className="ml-auto inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#4A6D82] text-white text-xs hover:bg-[#2E4959] transition"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Rafraîchir
          </button>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#DDE3E8] rounded-2xl overflow-hidden">
          {loading && (
            <div className="flex items-center justify-center py-16 text-[#6B7A85]">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement...
            </div>
          )}
          {!loading && data && data.subscribers.length === 0 && (
            <div className="text-center py-16 text-[#6B7A85]">
              <p className="font-serif text-lg">Aucun abonné pour ces filtres.</p>
            </div>
          )}
          {!loading && data && data.subscribers.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="subscribers-table">
                <thead className="bg-[#F7F5F0] text-[#4A6D82] text-xs uppercase tracking-widest">
                  <tr>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">Parcours</th>
                    <th className="text-left px-4 py-3">Étape</th>
                    <th className="text-left px-4 py-3">Statut</th>
                    <th className="text-left px-4 py-3">Prochain envoi</th>
                    <th className="text-left px-4 py-3">Inscrit</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.subscribers.map((sub) => (
                    <tr key={sub.id} className="border-t border-[#DDE3E8] hover:bg-[#F7F5F0]/60">
                      <td className="px-4 py-3">
                        <div className="font-medium text-[#1E2933]">{sub.email}</div>
                        {sub.name && <div className="text-xs text-[#6B7A85]">{sub.name}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-[#1E2933]">{sub.parcours_title}</div>
                        <div className="text-xs text-[#6B7A85]">{sub.parcours_id}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#E4EBEF] text-[#2E4959] text-xs font-medium">
                          {Math.min(sub.current_step, data.total_steps)}/{data.total_steps}
                        </span>
                      </td>
                      <td className="px-4 py-3"><StatusPill status={sub.status} /></td>
                      <td className="px-4 py-3 text-[#6B7A85] text-xs">{fmtDate(sub.next_send_at)}</td>
                      <td className="px-4 py-3 text-[#6B7A85] text-xs">{fmtDate(sub.enrolled_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1.5">
                          {sub.status === "active" && sub.current_step < data.total_steps && (
                            <button
                              onClick={() => act(sub, "sendNext")}
                              disabled={busy === sub.id + "sendNext"}
                              title="Envoyer l'étape courante maintenant"
                              data-testid={`send-next-${sub.id}`}
                              className="p-1.5 rounded-full text-[#4A6D82] hover:bg-[#E4EBEF] transition disabled:opacity-40"
                            >
                              {busy === sub.id + "sendNext" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                            </button>
                          )}
                          {sub.status === "active" && (
                            <button
                              onClick={() => act(sub, "pause")}
                              disabled={busy === sub.id + "pause"}
                              title="Mettre en pause"
                              className="p-1.5 rounded-full text-amber-600 hover:bg-amber-50 transition disabled:opacity-40"
                            >
                              <Pause className="w-4 h-4" />
                            </button>
                          )}
                          {sub.status === "paused" && (
                            <button
                              onClick={() => act(sub, "resume")}
                              disabled={busy === sub.id + "resume"}
                              title="Reprendre"
                              className="p-1.5 rounded-full text-emerald-600 hover:bg-emerald-50 transition disabled:opacity-40"
                            >
                              <Play className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => act(sub, "retrigger")}
                            disabled={busy === sub.id + "retrigger"}
                            title="Relancer la séquence depuis l'étape 1"
                            data-testid={`retrigger-${sub.id}`}
                            className="p-1.5 rounded-full text-[#4A6D82] hover:bg-[#E4EBEF] transition disabled:opacity-40"
                          >
                            {busy === sub.id + "retrigger" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`Supprimer ${sub.email} ?`)) act(sub, "delete");
                            }}
                            disabled={busy === sub.id + "delete"}
                            title="Supprimer"
                            className="p-1.5 rounded-full text-rose-600 hover:bg-rose-50 transition disabled:opacity-40"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>)}
      </main>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  if (!token) return <LoginView onAuthed={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}
